import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { authApi, demoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2, ChevronDown, Star, ShoppingBag, User } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_confusing-preview/artifacts/gu4q9ocf_trans_white_logo.png";

const TYPE_CONFIG = {
  creator: { icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  merchant: { icon: ShoppingBag, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  individual: { icon: User, color: 'text-green-500', bg: 'bg-green-500/10' },
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [demoUsers, setDemoUsers] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loginAsLoading, setLoginAsLoading] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    demoApi.getUsers().then(res => setDemoUsers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await authApi.login({ email, password });
      login(response.data.user, response.data.access_token);
      toast.success('Welcome back!');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginAs = async (user) => {
    setLoginAsLoading(user.wallet_id);
    setDropdownOpen(false);
    try {
      const res = await demoApi.loginAs(user.wallet_id);
      login(res.data.user, res.data.access_token);
      toast.success(`Logged in as ${user.full_name}`);
      navigate('/home');
    } catch (error) {
      toast.error('Failed to login');
    } finally {
      setLoginAsLoading(null);
    }
  };

  const formatFollowers = (n) => {
    if (!n) return null;
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="p-6 flex items-center justify-center">
        <img src={LOGO_URL} alt="LekeeWallet" className="h-12 w-auto" />
      </div>
      
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome back</h1>
            <p className="text-muted-foreground">Log in to your LekeeWallet</p>
          </div>
          
          {/* Quick Login - Profile Picker */}
          {demoUsers.length > 0 && (
            <div className="relative" ref={dropdownRef} data-testid="profile-picker">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-card border border-white/10 rounded-xl hover:border-primary/30 transition-colors"
                data-testid="profile-picker-trigger"
              >
                <span className="text-sm text-muted-foreground">Quick login as a demo user</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute z-50 w-full mt-2 bg-card border border-white/10 rounded-xl shadow-2xl shadow-black/50 max-h-72 overflow-y-auto" data-testid="profile-picker-dropdown">
                  {demoUsers.map((user) => {
                    const cfg = TYPE_CONFIG[user.user_type] || TYPE_CONFIG.individual;
                    const Icon = cfg.icon;
                    const followers = formatFollowers(user.follower_count);
                    return (
                      <button
                        key={user.wallet_id}
                        onClick={() => handleLoginAs(user)}
                        disabled={loginAsLoading === user.wallet_id}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left border-b border-white/5 last:border-0"
                        data-testid={`login-as-${user.wallet_id}`}
                      >
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 text-sm font-bold">{user.full_name?.charAt(0)}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm truncate">{user.full_name}</p>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color} capitalize flex items-center gap-0.5`}>
                              <Icon className="w-2.5 h-2.5" />{user.user_type}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.bio?.slice(0, 40) || user.email}{followers ? ` · ${followers} followers` : ''}
                          </p>
                        </div>
                        {loginAsLoading === user.wallet_id && <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-muted-foreground">or login with email</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-secondary border-white/10"
                data-testid="login-email-input"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-secondary border-white/10 pr-12"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">Join LekeeLekee</Link>
            </p>
            <Link to="/admin/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Admin Portal →</Link>
          </div>
        </div>
      </div>
      
      <div className="p-6 text-center">
        <p className="text-xs text-muted-foreground">Part of the LekeeLekee ecosystem</p>
      </div>
    </div>
  );
};

export default LoginPage;
